import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  TextField,
  IconButton,
  Button,
  Typography,
  useMediaQuery,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ImageIcon from "@mui/icons-material/Image";
import MovieIcon from "@mui/icons-material/Movie";
import ArticleIcon from "@mui/icons-material/Article";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import MediaTypeMenu from "./MediaTypemenu";
import {
  composerBottomPadSx,
  mobileNavMediaQuery,
  narrowChatSurfaceSx,
} from "../../../layout/layoutMetrics";

const SUPPORTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

export default function ChatInput({
  prompt,
  setPrompt,
  handleSend,
  mediaType,
  setMediaType,
  disabled = false,
  scriptOnly = false,
  showVideoNotificationPrompt = false,
  onDismissVideoNotificationPrompt,
  onNotifyVideoGenerated,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const fileInputRef = useRef(null);
  const isMobileNav = useMediaQuery(mobileNavMediaQuery);
  const canSend = Boolean(prompt.trim() || attachmentFile) && !disabled;

  useEffect(() => {
    if (!attachmentFile?.type?.startsWith("image/")) {
      setAttachmentPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(attachmentFile);
    setAttachmentPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [attachmentFile]);

  const submit = () => {
    if (!canSend) return;
    const fileToSend = attachmentFile;
    setAttachmentFile(null);
    handleSend(fileToSend);
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 720,
        mx: "auto",
        px: { xs: 0, sm: 2, md: 3 },
        pb: 0,
        pt: 2,
        flexShrink: 0,
        boxSizing: "border-box",
        [`@media ${mobileNavMediaQuery}`]: {
          ...narrowChatSurfaceSx,
          ...composerBottomPadSx,
        },
      }}
    >
      {showVideoNotificationPrompt && (
        <Box
          sx={{
            mb: 0.35,
            px: { xs: 1.6, sm: 2 },
            py: 1.05,
            minHeight: 50,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            borderRadius: "22px 22px 10px 10px",
            color: "white",
            background:
              "linear-gradient(135deg, rgba(12,12,13,0.72), rgba(24,24,26,0.58))",
            border: "1px solid rgba(255,255,255,0.1)",
            boxShadow:
              "0 12px 30px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.08)",
            backdropFilter: "blur(22px)",
            WebkitBackdropFilter: "blur(22px)",
            boxSizing: "border-box",
            [`@media ${mobileNavMediaQuery}`]: {
              borderRadius: "18px 18px 10px 10px",
              px: 1.35,
            },
          }}
        >
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: 13,
              fontWeight: 800,
              lineHeight: 1.25,
              color: "rgba(255,255,255,0.9)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Want to be notified when your video is ready?
          </Typography>

          <Button
            type="button"
            onClick={onNotifyVideoGenerated}
            sx={{
              minWidth: 74,
              px: 1.55,
              py: 0.62,
              borderRadius: "10px",
              fontSize: 12,
              fontWeight: 800,
              textTransform: "none",
              color: "rgba(24,25,28,0.94)",
              background: "rgba(255,255,255,0.92)",
              border: "1px solid rgba(255,255,255,0.78)",
              boxShadow: "0 8px 20px rgba(0,0,0,0.18)",
              "&:hover": {
                background: "rgba(255,255,255,0.98)",
              },
            }}
          >
            Notify
          </Button>

          <IconButton
            aria-label="Don't notify when video is generated"
            size="small"
            onClick={onDismissVideoNotificationPrompt}
            sx={{
              width: 28,
              height: 28,
              color: "rgba(255,255,255,0.52)",
              "&:hover": {
                color: "rgba(255,255,255,0.86)",
                background: "rgba(255,255,255,0.08)",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          background: "rgba(255,255,255,0.08)",
          borderRadius: 5,
          p: 1,
          border: "1px solid rgba(255,255,255,0.1)",
          backdropFilter: "blur(20px)",
          minHeight: 40,
          position: "relative",
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          overflow: "visible",
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept={SUPPORTED_IMAGE_TYPES.join(",")}
          disabled={disabled}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file && SUPPORTED_IMAGE_TYPES.includes(file.type)) {
              setAttachmentFile(file);
            }
            e.target.value = "";
          }}
        />
        {attachmentFile ? (
          <Box
            sx={{
              position: "relative",
              width: { xs: 112, sm: 128 },
              height: { xs: 112, sm: 128 },
              mt: 0.5,
              mb: 1,
              ml: 0.75,
              borderRadius: "18px",
              overflow: "hidden",
              bgcolor: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.12)",
              }}
            >
              {attachmentPreview ? (
                <Box
                  component="img"
                  src={attachmentPreview}
                  alt={attachmentFile.name}
                  sx={{
                    width: "100%",
                  height: "100%",
                  display: "block",
                    objectFit: "contain",
                  bgcolor: "rgba(0,0,0,0.18)",
                  }}
                />
              ) : (
                <AttachFileIcon
                sx={{
                  position: "absolute",
                  inset: 0,
                  m: "auto",
                  color: "rgba(255,255,255,0.8)",
                }}
              />
            )}
              <IconButton
                aria-label={`Remove ${attachmentFile.name}`}
                size="small"
                onClick={() => setAttachmentFile(null)}
                sx={{
                position: "absolute",
                top: 6,
                right: 6,
                p: "3px",
                color: "white",
                bgcolor: "rgba(0,0,0,0.55)",
                "&:hover": {
                  color: "white",
                  bgcolor: "rgba(0,0,0,0.75)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
          </Box>
        ) : null}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            width: "100%",
            minWidth: 0,
          }}
        >
        <IconButton
          type="button"
          aria-label={attachmentFile ? attachmentFile.name : "Add attachment"}
          disabled={disabled}
          onClick={() => fileInputRef.current?.click()}
          size="small"
          sx={{
            mr: 0.5,
            color: attachmentFile
              ? "rgba(255,255,255,0.9)"
              : "rgba(255,255,255,0.5)",
            bgcolor: attachmentFile
              ? "rgba(255,255,255,0.12)"
              : "transparent",
            borderRadius: "10px",
            width: 32,
            height: 32,
            flexShrink: 0,
            "&.Mui-disabled": {
              color: "rgba(255,255,255,0.3)",
            },
          }}
        >
          <AttachFileIcon fontSize="small" />
        </IconButton>

        <TextField
          fullWidth
          multiline
          minRows={1}
          maxRows={6}
          placeholder={`Describe the ${
            mediaType === "content" ? "script" : mediaType
          }...`}
          variant="standard"
          value={prompt}
          disabled={disabled}
          autoComplete="off"
          inputProps={{
            autoComplete: "off",
            "data-1p-ignore": true,
            "data-lpignore": "true",
          }}
          onChange={(e) => setPrompt(e.target.value)}
          onFocus={(e) => {
            if (!isMobileNav) return;
            requestAnimationFrame(() => {
              e.target?.scrollIntoView?.({
                block: "nearest",
                behavior: "smooth",
              });
            });
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && canSend) {
              e.preventDefault();
              submit();
            }
          }}
          InputProps={{
            disableUnderline: true,
            sx: { color: "white", ml: 1, minWidth: 0 },
          }}
          sx={{
            flex: "1 1 220px",
            minWidth: 0,
            [`@media ${mobileNavMediaQuery}`]: {
              flex: "1 1 120px",
            },
          }}
        />

        <Box
          sx={{
            ml: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexShrink: 0,
            position: "relative",
          }}
        >
          {!scriptOnly && (
            <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.8,
              px: 1.5,
              py: 0.5,
              fontSize: 11,
              fontWeight: 500,
              color: "rgba(255,255,255,0.5)",
              bgcolor:
                mediaType === "image"
                  ? "rgba(255,255,255,0.15)"
                  : "rgba(255,255,255,0.05)",
              borderRadius: "10px",
              border: "1px solid rgba(255,255,255,0.1)",
              minWidth: 70,
              justifyContent: "center",
            }}
          >
            {mediaType === "image" && (
              <ImageIcon sx={{ fontSize: 16 }} />
            )}
            {mediaType === "video" && (
              <MovieIcon sx={{ fontSize: 16 }} />
            )}
            {mediaType === "content" && (
              <ArticleIcon sx={{ fontSize: 16 }} />
            )}

            <span style={{ fontSize: 11 }}>
              {mediaType === "content"
                ? "Script"
                : mediaType.charAt(0).toUpperCase() + mediaType.slice(1)}
            </span>
          </Box>

          <IconButton
            type="button"
            aria-label="Select media type"
            aria-expanded={isExpanded}
            onClick={() => setIsExpanded(!isExpanded)}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.5)",
              bgcolor: "rgba(0,0,0,0.2)",
              borderRadius: "10px",
              width: 32,
              height: 32,
              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>

          {isExpanded && (
            <MediaTypeMenu
              mediaType={mediaType}
              setMediaType={setMediaType}
              setIsExpanded={setIsExpanded}
            />
              )}
            </>
          )}
        </Box>

        <IconButton
          onClick={submit}
          disabled={!canSend}
          sx={{
            ml: 1,
            color: "white",
            "&.Mui-disabled": {
              color: "rgba(255,255,255,0.3)",
            },
          }}
        >
          <SendIcon />
        </IconButton>
        </Box>
      </Box>
    </Box>
  );
}